import { useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Plus, Trash2, Calculator, Info, Copy, RotateCcw, Share2, FileDown, Save, FolderOpen, FilePlus2 } from "lucide-react";
import { toast } from "sonner";
import { generateFdiPDF } from "./fdi-pdf";
import { fmt } from "@/components/pipe/pipe-helpers";
import { SaveButton, FieldLabel } from "@/components/calculators/calc-helpers";
import { useJobLog } from "@/hooks/use-job-log";
import { useSavedWeldSets, type WeldEntry } from "@/hooks/use-saved-weld-sets";
import DataTable from "@/components/DataTable";
import {
  getWallFactor,
  materialOptions,
  weldTypeOptions,
  npsSizes,
  npsLabel,
  wallFactorHeaders, wallFactorRows,
  materialFactorHeaders, materialFactorRows,
  weldTypeHeaders, weldTypeRows,
  type WeldTypeId,
} from "./fdi-data";

const makeEntry = (): WeldEntry => ({
  id: crypto.randomUUID(),
  qty: 1,
  nps: 4,
  wall: "0.237",
  materialIdx: 0,
  weldTypeId: "butt",
  branchNps: 2,
  headerWall: "0.237",
  linearInches: "12",
});

const calcFDI = (e: WeldEntry): number => {
  const mat = materialOptions[e.materialIdx];
  const wt = weldTypeOptions.find(w => w.id === e.weldTypeId)!;
  const wallNum = parseFloat(e.wall) || 0;
  const wallFactor = getWallFactor(wallNum);

  if (e.weldTypeId === "linear") {
    const lin = parseFloat(e.linearInches) || 0;
    return lin * Math.PI * mat.factor * wallFactor * e.qty;
  }

  if (!wt.usesBranch) {
    return e.nps * wallFactor * mat.factor * wt.factor * e.qty;
  }

  let result = e.branchNps * wt.branchMultiplier * mat.factor;

  if (wt.usesHeaderWF) {
    const hdrWall = parseFloat(e.headerWall) || 0;
    result *= getWallFactor(hdrWall);
  } else if (wt.usesNozzleWF) {
    result *= wallFactor;
  }

  return result * e.qty;
};

export const FactoredWeldInchCalc = () => {
  const [entries, setEntries] = useState<WeldEntry[]>([makeEntry()]);
  const [jobName, setJobName] = useState("");
  const [showSaved, setShowSaved] = useState(false);
  const [activeSetId, setActiveSetId] = useState<string | null>(null);
  const { addEntry } = useJobLog();
  const { sets, saveSet, deleteSet, updateSet } = useSavedWeldSets();

  const update = useCallback((id: string, patch: Partial<WeldEntry>) => {
    setEntries(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e));
  }, []);

  const remove = (id: string) => setEntries(prev => prev.filter(e => e.id !== id));
  const addWeld = () => setEntries(prev => [...prev, makeEntry()]);
  const duplicate = (entry: WeldEntry) => setEntries(prev => [...prev, { ...entry, id: crypto.randomUUID() }]);
  const clearAll = () => { setEntries([makeEntry()]); setActiveSetId(null); };
  const newSet = () => { setEntries([makeEntry()]); setJobName(""); setActiveSetId(null); };

  const handleSaveSet = () => {
    const name = jobName.trim() || `Weld Set — ${new Date().toLocaleDateString()}`;
    if (activeSetId) {
      updateSet(activeSetId, entries);
      toast.success(`Updated "${name}"`);
    } else {
      const s = saveSet(name, entries);
      setActiveSetId(s.id);
      toast.success(`Saved "${name}"`);
    }
  };

  const handleLoadSet = (set: typeof sets[0]) => {
    setEntries(set.entries.map(e => ({ ...e, id: crypto.randomUUID() })));
    setJobName(set.name);
    setActiveSetId(set.id);
    setShowSaved(false);
    toast.success(`Loaded "${set.name}"`);
  };

  const handleDeleteSet = (id: string, name: string) => {
    deleteSet(id);
    if (activeSetId === id) setActiveSetId(null);
    toast("Deleted", { description: name });
  };

  const copyToClipboard = () => {
    const lines = entries.map((e, i) => {
      const wt = weldTypeOptions.find(w => w.id === e.weldTypeId)!;
      const mat = materialOptions[e.materialIdx];
      const fdi = calcFDI(e);
      if (e.weldTypeId === "linear") {
        return `#${i + 1}: ${e.qty}× Linear ${e.linearInches}" — ${mat.pNumber} — ${fmt(fdi, 1)} FDI`;
      }
      const base = `#${i + 1}: ${e.qty}× ${npsLabel(e.nps)} ${wt.label} — ${mat.pNumber}, Wall ${e.wall}"`;
      const branch = wt.usesBranch ? `, Branch ${npsLabel(e.branchNps)}` : "";
      return `${base}${branch} — ${fmt(fdi, 1)} FDI`;
    });
    const header = jobName ? `PFI TB-10 Factored Weld Inches — ${jobName}` : "PFI TB-10 Factored Weld Inches";
    const text = `${header}\n${"—".repeat(35)}\n${lines.join("\n")}\n${"—".repeat(35)}\nTotal: ${fmt(totalFDI, 1)} FDI`;
    navigator.clipboard.writeText(text).then(() => toast.success("Weld summary copied to clipboard"));
  };

  const totalFDI = entries.reduce((sum, e) => sum + calcFDI(e), 0);

  return (
    <div className="space-y-4">
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Calculator className="w-4 h-4 text-primary" />
            PFI TB-10 Factored Weld Inches
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            FDI = NPS × Wall Factor × Material Factor × Weld Type Factor
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Job name + save/load controls */}
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <FieldLabel>Project / Job #</FieldLabel>
              <Input placeholder="e.g. Job 2024-0312" value={jobName} onChange={ev => setJobName(ev.target.value)} className="bg-secondary text-foreground border-border h-9 text-sm" />
            </div>
            <Button variant="outline" size="sm" className="h-9 text-muted-foreground hover:text-primary" onClick={handleSaveSet} title={activeSetId ? "Update saved set" : "Save weld set"}>
              <Save className="w-4 h-4 mr-1" /> {activeSetId ? "Update" : "Save"}
            </Button>
            <Button variant="outline" size="sm" className="h-9 text-muted-foreground hover:text-primary" onClick={() => setShowSaved(!showSaved)} title="Load saved set">
              <FolderOpen className="w-4 h-4 mr-1" /> Load
            </Button>
            <Button variant="outline" size="sm" className="h-9 text-muted-foreground hover:text-primary" onClick={newSet} title="Start a new empty set">
              <FilePlus2 className="w-4 h-4 mr-1" /> New
            </Button>
          </div>

          {/* Saved sets panel */}
          {showSaved && (
            <Card className="bg-secondary/40 border-border/50">
              <CardContent className="py-2 space-y-1">
                <span className="text-xs font-bold text-muted-foreground">Saved Weld Sets</span>
                {sets.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2">No saved sets yet. Use "Save" to store your current welds.</p>
                ) : (
                  sets.map(s => {
                    const sFdi = s.entries.reduce((sum, e) => sum + calcFDI(e), 0);
                    return (
                      <div key={s.id} className={`flex items-center justify-between py-1.5 px-2 rounded-md cursor-pointer hover:bg-secondary/80 ${activeSetId === s.id ? "bg-primary/10 border border-primary/30" : ""}`}>
                        <div className="flex-1 min-w-0" onClick={() => handleLoadSet(s)}>
                          <p className="text-sm font-medium text-foreground truncate">{s.name}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {s.entries.length} weld(s) · {fmt(sFdi, 1)} FDI · {new Date(s.savedAt).toLocaleDateString()}
                          </p>
                        </div>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive shrink-0" onClick={() => handleDeleteSet(s.id, s.name)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          )}

          {entries.map((e, idx) => {
            const wt = weldTypeOptions.find(w => w.id === e.weldTypeId)!;
            const fdi = calcFDI(e);
            return (
              <Card key={e.id} className="bg-secondary/30 border-border/50">
                <CardContent className="pt-3 pb-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-muted-foreground">Weld #{idx + 1}</span>
                    <div className="flex items-center gap-1">
                      <span className="font-mono text-sm font-bold text-primary">{fmt(fdi, 1)} FDI</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-primary" onClick={() => duplicate(e)}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      {entries.length > 1 && (
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => remove(e.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <FieldLabel>Qty</FieldLabel>
                      <Input type="number" min={1} value={e.qty} onChange={ev => update(e.id, { qty: parseInt(ev.target.value) || 1 })} className="bg-secondary text-foreground border-border h-9 text-sm" />
                    </div>
                    {e.weldTypeId === "linear" ? (
                      <div>
                        <FieldLabel>Linear Inches</FieldLabel>
                        <Input type="number" step="0.1" min={0} value={e.linearInches} onChange={ev => update(e.id, { linearInches: ev.target.value })} className="bg-secondary text-foreground border-border h-9 text-sm" />
                      </div>
                    ) : (
                      <div>
                        <FieldLabel>NPS (in)</FieldLabel>
                        <Select value={String(e.nps)} onValueChange={v => update(e.id, { nps: parseFloat(v) })}>
                          <SelectTrigger className="bg-secondary text-foreground border-border h-9 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>{npsSizes.map(s => <SelectItem key={s} value={String(s)}>{npsLabel(s)}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <FieldLabel>Wall Thick. (in)</FieldLabel>
                      <Input type="number" step="0.001" value={e.wall} onChange={ev => update(e.id, { wall: ev.target.value })} className="bg-secondary text-foreground border-border h-9 text-sm" />
                    </div>
                    <div>
                      <FieldLabel>Material</FieldLabel>
                      <Select value={String(e.materialIdx)} onValueChange={v => update(e.id, { materialIdx: parseInt(v) })}>
                        <SelectTrigger className="bg-secondary text-foreground border-border h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>{materialOptions.map((m, i) => <SelectItem key={i} value={String(i)}>{m.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <FieldLabel>Weld Type</FieldLabel>
                    <Select value={e.weldTypeId} onValueChange={(v: WeldTypeId) => update(e.id, { weldTypeId: v })}>
                      <SelectTrigger className="bg-secondary text-foreground border-border h-9 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{weldTypeOptions.map(w => <SelectItem key={w.id} value={w.id}>{w.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>

                  {wt.usesBranch && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <FieldLabel>Branch NPS</FieldLabel>
                        <Select value={String(e.branchNps)} onValueChange={v => update(e.id, { branchNps: parseFloat(v) })}>
                          <SelectTrigger className="bg-secondary text-foreground border-border h-9 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>{npsSizes.map(s => <SelectItem key={s} value={String(s)}>{npsLabel(s)}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {wt.usesHeaderWF && (
                        <div>
                          <FieldLabel>Header Wall (in)</FieldLabel>
                          <Input type="number" step="0.001" value={e.headerWall} onChange={ev => update(e.id, { headerWall: ev.target.value })} className="bg-secondary text-foreground border-border h-9 text-sm" />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-2 text-[10px] text-muted-foreground pt-1">
                    <span>WF: {getWallFactor(parseFloat(e.wall) || 0)}</span>
                    <span>MF: {materialOptions[e.materialIdx].factor}</span>
                    <span>WT: {wt.usesBranch ? wt.description : wt.factor}</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1 border-dashed border-primary/30 text-muted-foreground hover:text-primary" onClick={addWeld}>
              <Plus className="w-4 h-4 mr-1" /> Add Weld
            </Button>
            {entries.length > 1 && (
              <Button variant="outline" size="sm" className="border-dashed border-destructive/30 text-muted-foreground hover:text-destructive" onClick={clearAll}>
                <RotateCcw className="w-4 h-4 mr-1" /> Clear All
              </Button>
            )}
          </div>

          {/* Total */}
          <Card className="bg-primary/10 border-primary/30">
            <CardContent className="py-3 flex justify-between items-center">
              <span className="text-sm font-semibold text-foreground">Total Factored Diameter Inches</span>
              <div className="flex items-center gap-1">
                <span className="font-mono text-xl font-bold text-primary">{fmt(totalFDI, 1)} FDI</span>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-primary hover:text-primary/80" onClick={copyToClipboard} title="Copy to clipboard">
                  <Share2 className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-primary hover:text-primary/80" onClick={() => generateFdiPDF(entries, jobName)} title="Export PDF">
                  <FileDown className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          <SaveButton
            onSave={(note) =>
              addEntry({
                calcType: "Factored Weld Inches",
                label: `${entries.length} weld(s) — ${fmt(totalFDI, 1)} FDI`,
                inputs: {
                  welds: String(entries.length),
                  jobName: jobName || "—",
                  details: entries.map((e, i) => `#${i + 1}: ${e.qty}× ${npsLabel(e.nps)} ${materialOptions[e.materialIdx].pNumber} ${weldTypeOptions.find(w => w.id === e.weldTypeId)!.label}`).join("; "),
                },
                results: [{ label: "Total FDI", value: fmt(totalFDI, 1), unit: "FDI" }],
                note,
              })
            }
          />
        </CardContent>
      </Card>

      {/* Reference tables */}
      <Accordion type="single" collapsible className="space-y-1">
        <AccordionItem value="wall" className="border-border/50">
          <AccordionTrigger className="text-xs font-semibold text-muted-foreground py-2">
            <span className="flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Wall Thickness Factor (Table D)</span>
          </AccordionTrigger>
          <AccordionContent className="-mx-4">
            <DataTable headers={wallFactorHeaders} rows={wallFactorRows} />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="material" className="border-border/50">
          <AccordionTrigger className="text-xs font-semibold text-muted-foreground py-2">
            <span className="flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Material Factor (Table A)</span>
          </AccordionTrigger>
          <AccordionContent className="-mx-4">
            <DataTable headers={materialFactorHeaders} rows={materialFactorRows} />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="weldtype" className="border-border/50">
          <AccordionTrigger className="text-xs font-semibold text-muted-foreground py-2">
            <span className="flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Weld Type Factor (Table E)</span>
          </AccordionTrigger>
          <AccordionContent className="-mx-4">
            <DataTable headers={weldTypeHeaders} rows={weldTypeRows} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
